import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import { Accordion, Box, Button, Flex, Select, Spinner, Text, useDisclosure } from '@chakra-ui/react'
import { MdAdd } from 'react-icons/md'
import { createProjectSection, updateProjectSection, deleteProjectSection, getProjectSectionsWithTasks } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { ModalSection, ModalRemoveSection, ModalSectionType } from './ModalSection'
import { ModalRemoveTask, ModalTask, ModalTaskType } from './ModalTask'
import { ProjectSectionRow } from './ProjectSectionRow'
import { DrawerProjectSectionTaskNote } from './DrawerProjectSectionTaskNote'

export const TASK_STATUSES = {
  Complete: 'Complete',
  Incomplete: 'Incomplete',
  All: 'All'
}

export const ProjectTasks = ({ projectId }) => {
  const { createToast } = useAppContext()
  const [isLoading, setIsLoading] = useState(false)
  const [sectionsWithTasks, setSectionsWithTasks] = useState([])

  const disclosureSection = useDisclosure()
  const disclosureRemoveSection = useDisclosure()
  const [selectedSection, setSelectedSection] = useState(null)

  const disclosureTask = useDisclosure()
  const disclosureRemoveTask = useDisclosure()
  const [selectedTask, setSelectedTask] = useState(null)

  const disclosureTaskNotes = useDisclosure()
  const [selectedTaskIdForNotes, setSelectedTaskIdForNotes] = useState(0)
  const [keyForNotesReload, setKeyForNotesReload] = useState(0)

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [filterTaskStatus, setFilterTaskStatus] = useState(TASK_STATUSES.Incomplete)

  useEffect(() => {
    loadData()
  }, [projectId])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await getProjectSectionsWithTasks(projectId)
      setSectionsWithTasks(data)
      setIsLoading(false)
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsLoading(false)
    }
  }, [projectId])

  const onAddSection = () => {
    disclosureSection.onOpen()
    setSelectedSection({})
  }

  const onEditSection = (section) => {
    disclosureSection.onOpen()
    setSelectedSection({ ...section })
  }

  const onRemoveSection = (section) => {
    disclosureRemoveSection.onOpen()
    setSelectedSection({ ...section })
  }

  const onAddTask = (sectionId) => {
    disclosureTask.onOpen()
    setSelectedTask({ sectionId })
  }

  const onEditTask = (task) => {
    disclosureTask.onOpen()
    setSelectedTask({ ...task })
  }

  const onRemoveTask = (task) => {
    disclosureRemoveTask.onOpen()
    setSelectedTask({ ...task })
  }

  const handleAddSection = async (data) => {
    setIsSubmitting(true)
    try {
      await createProjectSection({
        projectId,
        title: data.title
      })
      createToast('success', 'Success', 'Section added.')
      setIsSubmitting(false)
      disclosureSection.onClose()
      loadData()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  const handleEditSection = async (data) => {
    setIsSubmitting(true)
    try {
      await updateProjectSection(data.id, {
        projectId,
        title: data.title,
        sort: data.sort
      })
      createToast('success', 'Success', 'Section updated.')
      setIsSubmitting(false)
      disclosureSection.onClose()
      setSelectedSection({})
      loadData()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  const handleRemoveSection = async (id) => {
    setIsSubmitting(true)
    try {
      await deleteProjectSection(id)
      createToast('success', 'Success', 'Section removed.')
      setIsSubmitting(false)
      disclosureRemoveSection.onClose()
      loadData()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  const onOpenTaskNotes = (taskId) => {
    setSelectedTaskIdForNotes(taskId)
    disclosureTaskNotes.onOpen()
  }

  return (
    <>
      <Box>
        {isLoading ? (
          <Flex alignItems="center" gap={4}>
            <Spinner color="blue.400" size="sm" />
            <Text fontSize="sm">Loading ...</Text>
          </Flex>
        ) : (
          <>
            <Flex justifyContent="space-between">
              <Button
                colorScheme="blue"
                size="sm"
                onClick={onAddSection}
                leftIcon={<MdAdd fontSize="20px" />}
              >
                Add Section
              </Button>
              <Select value={filterTaskStatus} onChange={e => setFilterTaskStatus(e.target.value)} w="120px">
                {Object.values(TASK_STATUSES).map(v => <option key={v} value={v}>{v}</option>)}
              </Select>
            </Flex>
            <Accordion
              index={sectionsWithTasks.map((v, idx) => idx)}
              mt={sectionsWithTasks.length ? 6 : 0}
              allowMultiple
            >
              {sectionsWithTasks.map(section => (
                <ProjectSectionRow
                  key={section.id}
                  section={section}
                  onEditSection={onEditSection}
                  onRemoveSection={onRemoveSection}
                  onAddTask={onAddTask}
                  onEditTask={onEditTask}
                  onRemoveTask={onRemoveTask}
                  reload={loadData}
                  onOpenTaskNotes={onOpenTaskNotes}
                  filterTaskStatus={filterTaskStatus}
                />
              ))}
            </Accordion>
          </>
        )}
      </Box>
      <ModalSection
        modalType={!selectedSection?.id ? ModalSectionType.Add : ModalSectionType.Edit}
        section={selectedSection}
        handleAdd={handleAddSection}
        handleEdit={handleEditSection}
        isSubmitting={isSubmitting}
        {...disclosureSection}
      />
      <ModalRemoveSection
        section={selectedSection}
        isSubmitting={isSubmitting}
        handleRemove={handleRemoveSection}
        {...disclosureRemoveSection}
      />
      <ModalTask
        modalType={!selectedTask?.id ? ModalTaskType.Add : ModalTaskType.Edit}
        projectId={projectId}
        task={selectedTask}
        reload={() => {
          setSelectedTask(null)
          loadData()
          if (selectedTaskIdForNotes === selectedTask?.id) {
            setKeyForNotesReload(keyForNotesReload + 1)
          }
        }}
        {...disclosureTask}
      />
      <ModalRemoveTask
        task={selectedTask}
        reload={() => {
          setSelectedTask(null)
          loadData()
        }}
        {...disclosureRemoveTask}
      />
      <DrawerProjectSectionTaskNote
        keyForReload={keyForNotesReload}
        taskId={selectedTaskIdForNotes}
        onEditTask={onEditTask}
        {...disclosureTaskNotes}
      />
    </>
  )
}

ProjectTasks.propTypes = {
  projectId: PropTypes.number
}
